/**
 * English source strings. This file is the reference: every key added here must be added to ur.ts,
 * and the `Translations` type in index.ts makes a missing Urdu key a compile error rather than a
 * silent fallback to English at runtime.
 *
 * Keys are grouped by area, not by component, so the same label reused on five screens has one
 * translation. Interpolation uses {{name}} placeholders — never string concatenation, because word
 * order differs between English and Urdu.
 */
export const en = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    add: 'Add',
    search: 'Search',
    back: 'Back',
    confirm: 'Confirm',
    upload: 'Upload',
    view: 'View',
    download: 'Download',
    share: 'Share',
    pdf: 'PDF',
    date: 'Date',
    amount: 'Amount',
    notes: 'Notes',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    closed: 'Closed',
    type: 'Type',
    none: '—',
    loading: 'Loading…',
    noData: 'No data',
    required: 'This field is required.',
    language: 'Language',
    english: 'English',
    urdu: 'اردو',
  },

  nav: {
    investmentDashboard: 'Investment Dashboard',
    investors: 'Investors',
    myInvestmentDashboard: 'My Investment Dashboard',
    myInvestments: 'My Investments',
    myPortfolio: 'My Portfolio',
    reports: 'Reports',
    calculator: 'Calculator',
    users: 'Users',
    auditLog: 'Audit Log',
    logOut: 'Log out',
  },

  auth: {
    signIn: 'Sign in',
    username: 'Username',
    password: 'Password',
    usernameRequired: 'Username is required.',
    passwordRequired: 'Password is required.',
    appName: 'Investment System',
    sessionExpired: 'Your session has expired. Please sign in again.',
  },

  roles: {
    Admin: 'Admin',
    Accountant: 'Accountant',
    Viewer: 'Viewer',
    Investor: 'Investor',
    Dealer: 'Dealer',
  },

  tabs: {
    profile: 'Profile',
    investments: 'Investments',
    withdrawals: 'Withdrawals',
    profit: 'Profit Amount',
    ledger: 'Ledger',
    receipts: 'Receipts',
    overview: 'Overview',
  },

  party: {
    fullName: 'Full name',
    fatherName: "Father's name",
    mobile: 'Mobile',
    cnic: 'CNIC',
    address: 'Address',
    photo: 'Photo',
    cnicImage: 'CNIC image',
    agreementImage: 'Agreement image',
    agreementVideo: 'Agreement video',
    memberSince: 'Member since',
    description: 'Description',
    reason: 'Reason',
    recentTransactions: 'Recent transactions',
  },

  finance: {
    totalInvestment: 'Total Investment',
    totalInvestmentRemaining: 'Total Investment Remaining',
    investmentWithdrawn: 'Investment withdrawn',
    totalProfit: 'Total Profit',
    totalProfitRemaining: 'Total Profit Remaining',
    percentageInvestment: '% Investment',
    percentageProfitRemaining: '% Profit Remaining',
    fixedInvestment: 'Fixed Investment',
    fixedRemaining: 'Fixed Remaining',
    totalPaidProfit: 'Total Paid profit',
    remainingAmount: 'Remaining Amount',
    remainingInvestment: 'Remaining Investment',
    totalWithdrawn: 'Total withdrawn',
    totalPayable: 'Total payable amount',
    returnAgreed: 'Return Agreed Amount',
    profitRate: 'Monthly profit rate',
    paymentMethod: 'Payment method',
    accountNumber: 'Account / wallet number',
    transactionProof: 'Transaction proof',
    receiptNo: 'Receipt no',
    investment: 'Investment',
    withdrawal: 'Withdrawal',
    profitPayment: 'Profit Payment',
    amountPkr: 'Amount (PKR)',
    investmentDate: 'Investment date',
    withdrawalDate: 'Withdrawal date',
    paymentDate: 'Payment date',
    monthlyProfitRate: 'Monthly profit rate (%)',
    withdrawn: 'Withdrawn',
    remaining: 'Remaining',
    totalAmount: 'Total Amount',
    totalRemaining: 'Total Remaining',
    payableRemaining: 'Payable Remaining',
    totalPayableRemaining: 'Total Payable Remaining',
    receipt: 'Receipt',
    transactionProofOptional: 'Transaction proof (optional)',
    totalReturnAgreed: 'Total return amount agreed',
  },

  actions: {
    addInvestment: 'Add investment',
    withdrawFromInvestment: 'Withdraw from total investment',
    profitWithdrawn: 'Profit Withdrawn',
    recordPayment: 'Record profit payment',
  },

  table: {
    name: 'Name',
    code: 'Code',
    actions: 'Actions',
    role: 'Role',
    created: 'Created',
    username: 'Username',
    method: 'Method',
    rate: 'Rate',
    from: 'From',
    to: 'To',
    days: 'Days',
    debit: 'Debit',
    credit: 'Credit',
    tranche: 'Tranche',
    transactions: 'Transactions',
    nextDueDate: 'Next due date',
    asOf: 'As of',
  },

  forms: {
    father: 'Father',
    profilePhoto: 'Profile photo',
    investmentType: 'Investment type',
    fixedReturnAmount: 'Fixed return amount (PKR)',
    returnDueOn: 'Return due on',
    profitExpectedOn: 'Profit expected on',
    receiptNoAuto: 'Receipt no (auto-generated if blank)',
    newPasswordBlank: 'New password (leave blank to keep)',
    addInvestor: 'Add investor',
    addMyInvestment: 'Add my investment',
    addStaffUser: 'Add staff user',
    percentageHint: 'Monthly % based — profit accrues daily from a rate',
    percentageBook: 'Monthly % based',
    fixedBook: 'Fixed amount based',
    fixedInvestmentHint: 'Fixed investment — a set amount comes back',
  },

  dashboard: {
    totalInvestors: 'Total investors',
    today: 'Today',
    paidThisMonth: 'Paid this month',
    investmentPlusDebt: 'Investment + remaining debt',
    fixedInvestorsIn: 'Fixed-return — investors (money in)',
    fixedDealersOut: 'Fixed-return — my investments (money out)',
    profitPaid12: 'Profit paid — last 12 months',
    capitalInOut12: 'Capital paid in vs taken back — last 12 months',
    firstInvestment: 'First investment',
    initialDeposit: 'Initial deposit',
    additionalDeposits: 'Additional deposits',
    totalDeposited: 'Total deposited',
    paymentHistory: 'Payment history',
    profitTillNow: 'Total profit (till now)',
    profitPeriod: 'Total Profit (period)',
    investmentPlusProfit: 'Total Investment Remaining + Total Profit',
  },
  page: {
    accountAccess: 'Account & access',
    personalInformation: 'Personal information',
    editProfile: 'Edit profile',
    reissuePassword: 'Re-issue password',
    sendAccountDetails: 'Send account details',
    investorCode: 'Investor code',
    myInvestmentCode: 'My investment code',
    loginUsername: 'Login username',
    lastUpdated: 'Last updated',
    joined: 'Joined',
    documents: 'Documents',
    agreement: 'Agreement',
  },
  reports: {
    investorStatement: 'Investor statement',
    profit: 'Profit report',
    outstandingProfit: 'Outstanding profit report',
    daily: 'Daily report',
    monthly: 'Monthly report',
    yearly: 'Yearly report',
  },

  messages: {
    investmentAdded: 'Investment added.',
    withdrawalRecorded: 'Withdrawal recorded.',
    paymentRecorded: 'Profit payment recorded.',
    proofUploaded: 'Transaction proof uploaded.',
    amountMustBePositive: 'Amount must be greater than zero.',
    /** {{entered}} and {{available}} arrive pre-formatted with the PKR prefix. */
    overAmount:
      'Unable to process — {{entered}} is more than the {{label}} of {{available}}. Enter {{available}} or less.',
  },
} as const;
